import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { ServerDetail, ServerState } from '@kcdash/shared';
import { StatusBadge } from '../components/StatusBadge.js';
import {
  Network,
  Globe,
  Radio,
  Copy,
  Check,
  Save,
  RotateCw,
  ShieldCheck,
  Server,
  ExternalLink,
  Terminal,
  Info,
  AlertCircle,
  FileCode,
} from 'lucide-react';

export const NetworkSettingsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [server, setServer] = useState<ServerDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedCommand, setCopiedCommand] = useState(false);

  // Form fields
  const [domain, setDomain] = useState('');
  const [port, setPort] = useState(7777);
  const [queryPort, setQueryPort] = useState(7777);
  const [host, setHost] = useState('0.0.0.0');

  useEffect(() => {
    async function loadServer() {
      if (!id) return;
      try {
        const data = await api.getServer(id);
        setServer(data);
        setDomain(data.domain || '');
        setPort(data.port || 7777);
        setQueryPort(data.queryPort || data.port || 7777);
        setHost(data.host || '0.0.0.0');
      } catch (err: any) {
        setErrorMsg(err.message || 'Failed to load server network settings');
      } finally {
        setIsLoading(false);
      }
    }
    loadServer();
  }, [id]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;

    setIsSaving(true);
    setSuccessMsg('');
    setErrorMsg('');

    try {
      const updated = await api.updateServer(id, {
        domain: domain.trim() || undefined,
        port: Number(port),
        queryPort: Number(queryPort),
        host: host.trim() || '0.0.0.0',
      });

      setServer(updated);
      setSuccessMsg('Network configuration saved successfully! (server.toml and database updated)');

      if (server?.state === ServerState.ONLINE) {
        if (confirm('Port or network settings have been updated. Would you like to restart the server now to apply them?')) {
          await api.restartServer(id);
          const reloaded = await api.getServer(id);
          setServer(reloaded);
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Failed to update network settings');
    } finally {
      setIsSaving(false);
    }
  };

  const directAddress = `${domain.trim() || host || '127.0.0.1'}:${port}`;
  const connectCommand = `connect ${directAddress}`;

  const copyAddress = () => {
    navigator.clipboard.writeText(directAddress);
    setCopiedAddress(true);
    setTimeout(() => setCopiedAddress(false), 2000);
  };

  const copyCommand = () => {
    navigator.clipboard.writeText(connectCommand);
    setCopiedCommand(true);
    setTimeout(() => setCopiedCommand(false), 2000);
  };

  if (isLoading || !server) {
    return (
      <div className="py-20 text-center text-neutral-500 font-mono text-sm">
        Loading network configuration...
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Top Header Card */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-brand-950/70 border border-brand-800 text-brand-400 flex items-center justify-center">
              <Network className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-neutral-100">{server.name} — Network</h2>
                <StatusBadge state={server.state} size="md" />
              </div>
              <p className="text-xs text-neutral-400 font-mono mt-0.5">
                Configure IP address, port binding, public domain, and view player connection links
              </p>
            </div>
          </div>

          <Link
            to={`/servers/${id}/files?open=server.toml`}
            className="px-3.5 py-1.5 rounded-lg text-xs font-medium bg-panel-subtle hover:bg-neutral-800 border border-panel-border text-neutral-200 transition flex items-center gap-2 self-start md:self-auto"
          >
            <FileCode className="w-4 h-4 text-brand-400" />
            <span>View in server.toml</span>
          </Link>
        </div>
      </div>

      {/* Messages */}
      {successMsg && (
        <div className="p-4 rounded-xl bg-emerald-950/40 border border-emerald-800/80 text-emerald-300 text-xs flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-400 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 rounded-xl bg-red-950/40 border border-red-800/80 text-red-300 text-xs flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Direct Connection Box for Players */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-panel-border">
          <Globe className="w-5 h-5 text-brand-400" />
          <div>
            <h3 className="text-sm font-semibold text-neutral-100">Live Player Connection Information</h3>
            <p className="text-xs text-neutral-400">Share this connection address or in-game command with players</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Direct Address */}
          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/80 flex flex-col justify-between">
            <div>
              <span className="text-neutral-400 text-xs font-mono uppercase block mb-1">Direct Game Address</span>
              <div className="text-lg font-bold font-mono text-neutral-100 flex items-center justify-between gap-2 bg-panel-input px-3 py-2 rounded-lg border border-panel-border">
                <span className="truncate">{directAddress}</span>
                <button
                  onClick={copyAddress}
                  className="p-1 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                  title="Copy IP and Port"
                >
                  {copiedAddress ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 font-mono mt-3">
              Standard multiplayer target address for direct connection.
            </p>
          </div>

          {/* In-game Console Snippet */}
          <div className="bg-panel-subtle p-4 rounded-xl border border-panel-border/80 flex flex-col justify-between">
            <div>
              <span className="text-neutral-400 text-xs font-mono uppercase block mb-1">In-Game (F9) Console Command</span>
              <div className="text-sm font-bold font-mono text-brand-300 flex items-center justify-between gap-2 bg-panel-input px-3 py-2 rounded-lg border border-panel-border">
                <span className="truncate">{connectCommand}</span>
                <button
                  onClick={copyCommand}
                  className="p-1 rounded hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                  title="Copy in-game console command"
                >
                  {copiedCommand ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 font-mono mt-3">
              Players press <kbd className="px-1 py-0.5 bg-neutral-800 rounded text-neutral-300">F9</kbd> in Kingdom Come II and paste this command.
            </p>
          </div>
        </div>
      </div>

      {/* Network Configuration Form */}
      <form onSubmit={handleSave} className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm space-y-6">
        <div className="flex items-center gap-2 pb-3 border-b border-panel-border">
          <Radio className="w-5 h-5 text-brand-400" />
          <div>
            <h3 className="text-sm font-semibold text-neutral-100">Network & Port Configuration</h3>
            <p className="text-xs text-neutral-400">Configure host bind IP, UDP game ports, and public DNS domain</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Domain / Public Host */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-300">
              Public Domain or IP Address
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="e.g. kcdash.xoam24.com or 192.168.1.100"
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-brand-500 font-mono"
            />
            <p className="text-[11px] text-neutral-500">
              The public DNS domain or external IP shown to players in the server list and connection guides.
            </p>
          </div>

          {/* Game Port */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-300">
              Game Port (UDP / TCP)
            </label>
            <input
              type="number"
              min={1024}
              max={65535}
              value={port}
              onChange={(e) => setPort(parseInt(e.target.value, 10) || 7777)}
              required
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-brand-500 font-mono"
            />
            <p className="text-[11px] text-neutral-500">
              Primary game port (default: 7777). Synchronizes with <code className="text-brand-400">port = {port}</code> in server.toml.
            </p>
          </div>

          {/* Bind IP */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-300">
              Server Bind Host IP
            </label>
            <input
              type="text"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="0.0.0.0"
              required
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-brand-500 font-mono"
            />
            <p className="text-[11px] text-neutral-500">
              Use <code className="text-neutral-300">0.0.0.0</code> to listen on all host network interfaces.
            </p>
          </div>

          {/* Query Port */}
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-neutral-300">
              Query / Status Port (TCP)
            </label>
            <input
              type="number"
              min={1024}
              max={65535}
              value={queryPort}
              onChange={(e) => setQueryPort(parseInt(e.target.value, 10) || port)}
              className="w-full px-3 py-2 rounded-lg bg-panel-input border border-panel-border text-sm text-neutral-100 placeholder-neutral-500 focus:outline-none focus:border-brand-500 font-mono"
            />
            <p className="text-[11px] text-neutral-500">
              TCP port for KCD:MP JSON status (<code className="text-neutral-300">http://&lt;host&gt;:{port}/</code>).
            </p>
          </div>
        </div>

        <div className="pt-4 border-t border-panel-border flex items-center justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white disabled:opacity-40 transition shadow-sm"
          >
            <Save className={`w-4 h-4 ${isSaving ? 'animate-spin' : ''}`} />
            <span>{isSaving ? 'Saving Changes...' : 'Save Network Configuration'}</span>
          </button>
        </div>
      </form>

      {/* Firewall & Port Routing Matrix */}
      <div className="bg-panel-card border border-panel-border rounded-xl p-6 shadow-sm space-y-4">
        <div className="flex items-center gap-2 pb-3 border-b border-panel-border">
          <ShieldCheck className="w-5 h-5 text-brand-400" />
          <div>
            <h3 className="text-sm font-semibold text-neutral-100">Firewall & Port Routing Matrix</h3>
            <p className="text-xs text-neutral-400">Status of required network ports on this Linux host</p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead>
              <tr className="border-b border-panel-border text-neutral-400">
                <th className="pb-2 font-medium">SERVICE</th>
                <th className="pb-2 font-medium">PORT</th>
                <th className="pb-2 font-medium">PROTOCOL</th>
                <th className="pb-2 font-medium">DIRECTION</th>
                <th className="pb-2 font-medium text-right">STATUS</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-panel-border/50 text-neutral-300">
              <tr>
                <td className="py-2.5 font-semibold text-neutral-100">KCD:MP Game Traffic</td>
                <td className="py-2.5 text-brand-400">{port}</td>
                <td className="py-2.5">UDP (ENet)</td>
                <td className="py-2.5">Inbound</td>
                <td className="py-2.5 text-right text-emerald-400 font-semibold">● ACTIVE (UFW Allowed)</td>
              </tr>
              <tr>
                <td className="py-2.5 font-semibold text-neutral-100">KCD:MP Relay & Status</td>
                <td className="py-2.5 text-brand-400">{port}</td>
                <td className="py-2.5">TCP</td>
                <td className="py-2.5">Inbound</td>
                <td className="py-2.5 text-right text-emerald-400 font-semibold">● ACTIVE (UFW Allowed)</td>
              </tr>
              <tr>
                <td className="py-2.5 font-semibold text-neutral-100">Web Dashboard & API</td>
                <td className="py-2.5 text-neutral-300">80, 443</td>
                <td className="py-2.5">TCP (HTTPS)</td>
                <td className="py-2.5">Inbound</td>
                <td className="py-2.5 text-right text-emerald-400 font-semibold">● SECURE (SSL Proxy)</td>
              </tr>
              <tr>
                <td className="py-2.5 font-semibold text-neutral-100">Host Agent IPC</td>
                <td className="py-2.5 text-neutral-300">7790 / Socket</td>
                <td className="py-2.5">TCP / Unix Socket</td>
                <td className="py-2.5">Loopback Only</td>
                <td className="py-2.5 text-right text-neutral-400">● INTERNAL</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
